import React, { useState } from 'react';
import {
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { COLORS } from '../../../shared/colors';
import { PROMPT_TYPES } from '../../../shared/mockData';

const TemplateRow = ({ 
  template, 
  onEdit, 
  onDelete, 
  onSetActive 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editData, setEditData] = useState({
    name: template.name,
    type: template.type,
    content: template.content
  });
  
  const maxLength = 300;
  const isLongContent = template.content.length > maxLength;
  
  const handleToggleActive = (event) => {
    onSetActive(template.id, template.type, event.target.checked);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditData({ name: template.name, type: template.type, content: template.content });
  };

  const handleSave = () => {
    onEdit({ ...template, ...editData });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({ name: template.name, type: template.type, content: template.content });
  };

  const handleChange = (field) => (event) => {
    setEditData(prev => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <div className={`template-row ${template.isActive ? 'active' : ''} ${isEditing ? 'editing' : ''}`}>
      <div className="template-row-header">
        <div className="template-info">
          {isEditing ? (
            <Box display="flex" gap={3} width="100%">
              <TextField
                label="Template Name"
                placeholder="Enter template name"
                value={editData.name}
                onChange={handleChange('name')}
                sx={{ minWidth: 300, flex: 1 }}
              />
              
              <FormControl sx={{ minWidth: 300, flex: 1 }}>
                <InputLabel>Prompt Type</InputLabel>
                <Select
                  value={editData.type}
                  onChange={handleChange('type')}
                  label="Prompt Type"
                  displayEmpty
                >
                  {(PROMPT_TYPES || []).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                {template.name}
              </Typography>
              <Chip 
                label={template.isActive ? 'Active' : 'Inactive'}
                size="small"
                sx={{ 
                  bgcolor: template.isActive ? COLORS.success : COLORS.gray[200],
                  color: template.isActive ? 'white' : COLORS.gray[600],
                  fontWeight: 500
                }}
              />
            </>
          )}
        </div>
        
        <div className="template-actions">
          <div className="toggle-container">
            <FormControlLabel
              control={
                <Switch
                  checked={template.isActive}
                  onChange={handleToggleActive}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: COLORS.success,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: COLORS.success,
                    },
                  }}
                />
              }
              label={
                <span className="toggle-label">
                  {template.isActive ? 'Active' : 'Inactive'}
                </span>
              }
            />
          </div>
          
          {isEditing ? (
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ 
                  bgcolor: COLORS.success,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { 
                    bgcolor: '#218838'
                  }
                }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                sx={{ 
                  color: COLORS.gray[600],
                  borderColor: COLORS.gray[300],
                  textTransform: 'none',
                  px: 3,
                  '&:hover': { 
                    bgcolor: COLORS.gray[50],
                    borderColor: COLORS.gray[400]
                  }
                }}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <>
              <Tooltip title="Edit Template">
                <IconButton
                  onClick={handleEditClick}
                  sx={{ 
                    color: COLORS.gray[600],
                    '&:hover': { 
                      bgcolor: COLORS.blue[50],
                      color: COLORS.primary
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Delete Template">
                <IconButton
                  onClick={() => onDelete(template.id)}
                  sx={{ 
                    color: COLORS.gray[600],
                    '&:hover': { 
                      bgcolor: COLORS.red[50],
                      color: COLORS.error
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      
      <div className="template-content">
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={Math.max(3, Math.min(30, editData.content.split('\n').length + 1))}
            value={editData.content}
            onChange={handleChange('content')}
            placeholder="Enter your prompt template..."
            sx={{ 
              mb: 3,
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                lineHeight: 1.5,
                fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
              },
              '& .MuiInputBase-input': {
                resize: 'vertical'
              }
            }}
          />
        ) : (
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6b7280',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                maxHeight: isLongContent && !isExpanded ? '120px' : 'none',
                overflow: isLongContent && !isExpanded ? 'hidden' : 'visible',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e9ecef'
              }}
            >
              {isLongContent && !isExpanded 
                ? `${template.content.substring(0, maxLength)}...`
                : template.content
              }
            </Typography>
            {isLongContent && (
              <Button
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ 
                  mt: 1,
                  color: COLORS.primary,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                {isExpanded ? 'Read Less' : 'Read More'}
              </Button>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default TemplateRow;